import { useLazyQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import Activity from "@components/Activity";
import TertiaryButton from "@components/buttons/TertiaryButton";
import styles from "@components/expert/ExpertActivitiesPage.module.css";
import ExpertRoleBoxes from "@components/ExpertRoleBoxes";
import ActivityFilters from "@components/forms/ActivityFilters";
import HandleQuery from "@components/HandleQuery";
import InfiniteScroll from "@components/InfiniteScroll";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import HugeText from "@components/text/HugeText";
import LargeText from "@components/text/LargeText";
import useSubscribeToExpertActivities from "@hooks/subscriptions/useSubscribeToExpertActivities";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { OtherActivityKindsEnum, StartTimeValuesEnum } from "@typing/Enums";
import {
  ActivityActorKindEnum,
  ExpertGlobalRoleEnum,
  GetActivitiesDocument,
  GetActivitiesQuery,
  GetActivitiesQueryVariables,
  GetActivitySummaryDocument,
  GetActivitySummaryQuery,
  GetActivitySummaryQueryVariables
} from "@typing/Generated";
import {
  calculateActivityKindsFromEnum,
  calculateStartTimeFromEnum,
  filterActivityGroupWithOneArticle,
  groupActivitiesByJourneyId
} from "@utils/modelUtils";

type Filters = ComponentProps<typeof ActivityFilters>["filters"];

const ExpertActivitiesPage = () => {
  const myExpert = useMyExpert();

  const [getActivitySummary, getActivitySummaryQuery] = useLazyQuery<
    GetActivitySummaryQuery,
    GetActivitySummaryQueryVariables
  >(GetActivitySummaryDocument);

  const [getActivities, getActivitiesQuery] = useLazyQuery<GetActivitiesQuery, GetActivitiesQueryVariables>(
    GetActivitiesDocument
  );

  useSubscribeToExpertActivities(myExpert?.id, () => {
    getActivitiesQuery.refetch();
    getActivitySummaryQuery.refetch();
  });

  const [filters, setFilters] = useState<Filters>({
    activityKind: OtherActivityKindsEnum.ALL,
    actorKind: ActivityActorKindEnum.clients,
    clientId: null,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? null : myExpert?.id,
    section: null,
    startTime: StartTimeValuesEnum.PAST_SEVEN_DAYS
  });

  useEffect(() => {
    if (myExpert) {
      getActivitySummary({
        variables: {
          clientId: filters.clientId,
          expertId: filters.expertId,
          startTime: calculateStartTimeFromEnum(filters.startTime)
        }
      });
    }
  }, [filters.clientId, filters.expertId, filters.startTime, getActivitySummary, myExpert]);

  useEffect(() => {
    if (myExpert) {
      getActivities({
        variables: {
          activityKinds: calculateActivityKindsFromEnum(filters.activityKind),
          actorKind: filters.actorKind,
          clientId: filters.clientId,
          expertId: filters.expertId,
          section: filters.section,
          startTime: calculateStartTimeFromEnum(filters.startTime)
        }
      });
    }
  }, [filters, getActivities, myExpert]);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const allActivities = useNodesFromConnection(getActivitiesQuery.data?.activities);
  const activitiesGrouped = useMemo(() => {
    const groups = groupActivitiesByJourneyId(allActivities);
    return Object.values(groups);
  }, [allActivities]);

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.activity.list">
      <div className={styles.goBack}>
        <TertiaryButton icon="chevronLeft" routerLink="/expert/dashboard" textKey="pages.expert.activity.dashboard" />
      </div>
      <div className={styles.title}>
        <HugeText>
          <FormattedMessage id="pages.expert.activity.activity" />
        </HugeText>
      </div>
      <div className="ion-margin-start ion-margin-end">
        <ActivityFilters
          activitySummary={getActivitySummaryQuery.data?.activitySummary}
          filters={filters}
          inDashboard={false}
          onChange={handleFilterChange}
        />
      </div>

      <HandleQuery query={getActivitySummaryQuery}>
        {_activitySummaryData => (
          <HandleQuery query={getActivitiesQuery}>
            {_activitiesData => (
              <InfiniteScroll
                loadMore={getActivitiesQuery.fetchMore}
                pageInfo={getActivitiesQuery.data?.activities.pageInfo}
              >
                <div className={styles.tableContainer} />
                <div className={styles.filters} />

                <div className={styles.activitiesContainer}>
                  {activitiesGrouped.map(group => (
                    <div className={styles.group} key={group.journeyId}>
                      <IonGrid className="ion-no-padding">
                        <IonRow className="ion-align-items-center ion-no-padding">
                          <IonCol className="ion-margin-end" size="auto">
                            <LargeText className={styles.owner}>{group.journey?.owner.fullName}</LargeText>
                          </IonCol>
                          <IonCol>
                            <ExpertRoleBoxes expertRoles={group.journey?.expertRoles} />
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                      {filterActivityGroupWithOneArticle(group).models.map(activity => (
                        <Activity activitiesForGroup={group.models} activity={activity} key={activity.id} />
                      ))}
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertActivitiesPage;
